/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { LinkButton, ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { translate } from 'react-i18next';
import { Tooltip, Columns, Button, Box } from '@locus-taxy/neo-ui';

import defaultTheme from './BulkViewEmptyState.scss';
import {
    type RoleOperationEnumType,
    PermissionMatch,
    AccessDeniedMessage
} from 'common/modules/rbac';
import type { TFunction } from 'react-i18next';

type Props = {
    theme: Object,
    imageSrc: string,
    title: string,
    disableSubtitle: boolean,
    subtitle: string,
    disableAddButton: boolean,
    entityNameId: ?string,
    onAddClick: ?() => void,
    onImportClick: ?() => void,
    onDeleteClick?: () => void,
    disableButtonLabel?: string,
    redirectOptions?: { buttonLabel: string, url: string },
    t: TFunction,
    operations: {
        add: RoleOperationEnumType,
        edit: RoleOperationEnumType,
        export: RoleOperationEnumType
    }
};

class BulkViewEmptyState extends React.PureComponent<Props> {
    static defaultProps = {
        disableAddButton: false
    };
    render() {
        const {
            imageSrc,
            title,
            subtitle,
            disableAddButton,
            t,
            onAddClick,
            onImportClick,
            onDeleteClick,
            theme,
            entityNameId,
            redirectOptions,
            disableSubtitle,
            disableButtonLabel,
            operations
        } = this.props;
        const emptyStateTitle =
            title || t('entityEmptyStateTitle', { entityNameId });

        const emptyStateSubtitle = disableSubtitle
            ? null
            : subtitle ||
              t('entityEmptyStateSubtitle', {
                  entityNameId
              });
        return (
            <div className={theme.wrap}>
                <img className={theme.emptyImage} src={imageSrc} />
                <span className={theme.title}>{emptyStateTitle}</span>
                <span className={theme.subtitle}>{emptyStateSubtitle}</span>
                <Box marginTop={4}>
                    <Columns space="medium">
                        {!disableAddButton ? (
                            <PermissionMatch
                                operations={operations.add}
                                render={hasPermission => {
                                    const action = t('addEntity', {
                                        entityNameId
                                    });
                                    return (
                                        <Tooltip
                                            tooltip={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={action}
                                                    />
                                                ) : null
                                            }
                                            shouldWrapChildren
                                        >
                                            <Button
                                                palette="primary"
                                                appearance="solid"
                                                icon="add"
                                                onClick={onAddClick}
                                                disabled={!hasPermission}
                                            >
                                                {action}
                                            </Button>
                                        </Tooltip>
                                    );
                                }}
                            />
                        ) : null}
                        {onImportClick ? (
                            <PermissionMatch
                                operations={operations.add}
                                render={hasPermission => {
                                    const action = t(
                                        'entityImport.importEntity',
                                        {
                                            entityNameId
                                        }
                                    );
                                    return (
                                        <Tooltip
                                            tooltip={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={action}
                                                    />
                                                ) : null
                                            }
                                            shouldWrapChildren
                                        >
                                            <Button
                                                palette="primary"
                                                appearance="solid"
                                                icon="import"
                                                onClick={onImportClick}
                                                disabled={!hasPermission}
                                            >
                                                {action}
                                            </Button>
                                        </Tooltip>
                                    );
                                }}
                            />
                        ) : null}
                        {onDeleteClick ? (
                            <PermissionMatch
                                operations={operations.edit}
                                render={hasPermission => {
                                    return (
                                        <Tooltip
                                            tooltip={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={
                                                            disableButtonLabel
                                                        }
                                                    />
                                                ) : null
                                            }
                                            shouldWrapChildren
                                        >
                                            <Button
                                                palette="danger"
                                                appearance="solid"
                                                icon="delete"
                                                onClick={onDeleteClick}
                                                disabled={!hasPermission}
                                            >
                                                {disableButtonLabel}
                                            </Button>
                                        </Tooltip>
                                    );
                                }}
                            />
                        ) : null}
                        {redirectOptions ? (
                            <LinkButton
                                semibold
                                uppercase
                                color={dashboardColors.darkSkyBlue.name}
                                theme={theme}
                                buttonType={ButtonTypes.Solid}
                                label={redirectOptions.buttonLabel}
                                href={redirectOptions.url}
                            />
                        ) : null}
                    </Columns>
                </Box>
            </div>
        );
    }
}

export default translate('entityManagement')(
    themr('BulkViewEmptyState', defaultTheme)(BulkViewEmptyState)
);
